import { isEqual } from 'lodash-es';
import type { ListQueryVariables } from '~/lib/goliath/goliath.types';
import config, { type UrlFilters } from './config';

const helpers = {
  rightTrimArray(items: (string | undefined)[]) {
    const lastPresentIndex = items.map((v) => v !== undefined).lastIndexOf(true);
    if (lastPresentIndex < 0) return [];
    return items.slice(0, lastPresentIndex + 1);
  },
  wrapArray(value: string | string[] | undefined | null) {
    if (value === undefined || value === null) return [];
    return Array.isArray(value) ? value : [value];
  },
};

export default class Serializer {
  private vars;

  private readonly filter;

  private readonly pagination;

  constructor(vars: ListQueryVariables) {
    this.vars = vars || {};
    this.filter = this.vars.filter || {};
    this.pagination = this.vars.pagination || {};
  }

  getRoute() {
    return {
      path: this.getPath(),
      query: this.getQueryParams(),
    };
  }

  // Path Params

  getPath() {
    const pathSegments = `/${this.getPathSegments()}`;
    if (config.pathPrefix) {
      return `${config.pathPrefix}${pathSegments}`;
    }
    return pathSegments;
  }

  getPathParams() {
    return { config: this.getPathSegments() };
  }

  getPathSegments() {
    const segments = [
      ...this.getCategoryPathSegments(),
      this.getFilterPathSegment(),
      this.getPagePathSegment(),
    ];

    return segments.filter(Boolean).join(config.separators.path);
  }

  getCategoryPathSegments() {
    const paths = (this.filter.productCategoryPaths || []).filter(Boolean);
    if (!paths.length) return [];

    const pathSegments = paths.map((path) => path.split(config.separators.path));
    const segLength = pathSegments[0].length;

    if (!pathSegments.every((s) => s.length === segLength)) {
      throw new Error(
        'Multiple categories must have a common parent in the product ' +
          `category filter (got: ${paths})`
      );
    }

    const parentCategories = pathSegments
      .map((s) => s.slice(0, segLength - 1))
      .filter(
        (value, index, self) =>
          index === self.findIndex((t) => JSON.stringify(t) === JSON.stringify(value))
      );

    if (parentCategories.length > 1) {
      throw new Error(
        'Multiple categories must have a common parent in the product ' +
          `category filter (got: ${paths})`
      );
    }

    const childCategorySlugs = pathSegments.map((s) => s[s.length - 1]).sort();

    return [...parentCategories[0], childCategorySlugs.join(config.separators.item)];
  }

  getFilterPathSegment() {
    let segments = helpers.rightTrimArray(
      config.filter.positions.map((name) => this.getFilterValue(name))
    );

    if (!segments.length) return undefined;

    segments = segments.map((value, index) => {
      const filterName = config.filter.positions[index];
      return value || config.filter.placeholders[filterName];
    });

    const str = segments.join(config.separators.filter);
    return `${config.prefixes.filter}${str}`;
  }

  getFilterValue(name: UrlFilters) {
    const values = helpers.wrapArray(this.filter[name]);
    if (!values.length) return undefined;
    const prefix = config.filter.prefixes[name];
    const valueStr = values.sort().join(config.separators.item);
    if (prefix) return `${prefix}${valueStr}`;
    return valueStr;
  }

  getPagePathSegment() {
    if (!this.pagination) return undefined;
    const { page } = this.pagination;
    if (!page || page === 1) return undefined;
    return `${config.prefixes.page}${page}`;
  }

  // Query Params

  getQueryParams() {
    return {
      discounted: this.getDiscountedFilter(),
      minPrice: this.getMinPrice(),
      maxPrice: this.getMaxPrice(),
      pageSize: this.getPageSize(),
      search: this.getSearchTerm(),
      sort: this.getSort(),
      productProviders: this.getProductProviders(),
      productTagSlugs: this.getProductTagSlugs(),
      anyInGroupOfProductTagSlugs: this.getAnyInGroupOfProductTagSlugs(),
    };
  }

  getDiscountedFilter() {
    switch (this.filter.discounted) {
      case true:
        return 'true';
      case false:
        return 'false';
      default:
        return undefined;
    }
  }

  getMinPrice() {
    const price = this.filter.onetimePrice;
    if (!price || !price.min) return undefined;
    return price.min.toString();
  }

  getMaxPrice() {
    const price = this.filter.onetimePrice;
    if (!price || !price.max) return undefined;
    return price.max.toString();
  }

  getPageSize() {
    const pageSize = this.pagination.size;
    if (!pageSize || pageSize === config.defaultPageSize) return undefined;
    return pageSize.toString();
  }

  getSearchTerm() {
    return this.vars.search || undefined;
  }

  getSort() {
    const defaultSort = config.sortMap[config.defaultSort];
    if (isEqual(this.vars.sort, defaultSort)) return undefined;

    return Object.keys(config.sortMap).find((name) =>
      isEqual(this.vars.sort, config.sortMap[name])
    );
  }

  getProductProviders() {
    return this.vars.filter?.productProviders || [];
  }

  getProductTagSlugs() {
    return this.vars.filter?.productTagSlugs || [];
  }

  getAnyInGroupOfProductTagSlugs() {
    return this.vars.filter?.anyInGroupOfProductTagSlugs || [];
  }
}
